import type { ClerkProvider } from '@clerk/react';
import type { ComponentProps } from 'react';
import type { Scheme } from './theme';

/**
 * Clerk's components, painted in the console's palette.
 *
 * Clerk draws its own DOM with its own colours, so the `dark:` variant
 * that dresses everything else never reaches inside: a popover it opens
 * over a dark sidebar comes up white, and the name on its trigger comes
 * up near-black on the sidebar's dark ground, because Clerk colours that
 * span itself rather than inheriting from the button we styled. Telling
 * Clerk the palette through `variables` is the only way in, and it has
 * to be told AGAIN whenever the scheme changes — these are values, not
 * CSS, and Clerk derives its hover, border and ring shades from them.
 *
 * The values are the headless design system's tokens
 * (src/theme/headless/tokens/colors.css), copied as literals because
 * Clerk parses them to derive its own shades; `CLERK_TOKENS` names the
 * token each one is copied from, and clerk-appearance.test.ts fails
 * when a value and its token part:
 *
 * - `colorBackground`: `--bg-raised`, the step menus and dialogs sit on,
 *   so the organisation popover agrees with the site menu beside it and
 *   the sign-in card sits a step above its ground.
 * - `colorForeground` / `colorMutedForeground`: `--text-1` / `--text-2`
 *   (`text-fg` / `text-fg-2`).
 * - `colorNeutral`: the base Clerk fades for borders, dividers and hover —
 *   white in dark, the ground's #131416 in light, the bases the
 *   hairline tokens' alphas are cut from.
 * - `colorPrimary` / `colorPrimaryForeground`: the steel accent and the
 *   text on it — Clerk's button is the sign-in page's primary action.
 * - `colorInput`: `--fill-control` in light; in dark that fill is
 *   a translucent white, so the solid `--bg-surface` stands in, an inset
 *   step below the raised card.
 * - status, focus ring and scrim: `--danger`, `--good`, `--warn`,
 *   `--focus-ring`, `--backdrop` (the ring and scrim reach Clerk faded:
 *   `CORE2_FADE` below).
 */
/** @clerk/react does not export Clerk's `Appearance`; the provider's
 *  prop is the same type by another road. */
type Appearance = NonNullable<ComponentProps<typeof ClerkProvider>['appearance']>;

type Variables = NonNullable<Appearance['variables']>;

/** The token each Clerk colour is copied from, per scheme (a name, or
 *  the ramp step where the scheme's value is a raw ramp colour). */
export const CLERK_TOKENS: Record<Scheme, Record<string, string>> = {
  light: {
    colorBackground: '--bg-raised',
    colorForeground: '--text-1',
    colorMutedForeground: '--text-2',
    colorNeutral: '--hl-neutral-900',
    colorPrimary: '--accent',
    colorPrimaryForeground: '--text-on-accent',
    colorInput: '--fill-control',
    colorInputForeground: '--text-1',
    colorDanger: '--danger',
    colorSuccess: '--good',
    colorWarning: '--warn',
    colorRing: '--focus-ring',
    colorModalBackdrop: '--backdrop',
  },
  dark: {
    colorBackground: '--bg-raised',
    colorForeground: '--text-1',
    colorMutedForeground: '--text-2',
    colorNeutral: '--hl-neutral-0',
    colorPrimary: '--accent',
    colorPrimaryForeground: '--text-on-accent',
    colorInput: '--bg-surface',
    colorInputForeground: '--text-1',
    colorDanger: '--danger',
    colorSuccess: '--good',
    colorWarning: '--warn',
    colorRing: '--focus-ring',
    colorModalBackdrop: '--backdrop',
  },
};

/** Plex Sans and the 6px control radius, as everywhere else. */
const SHARED: Variables = {
  fontFamily: '"IBM Plex Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
  borderRadius: '6px',
};

export const PALETTE: Record<Scheme, Variables> = {
  light: {
    ...SHARED,
    colorBackground: '#e6e8eb',
    colorForeground: '#131416',
    colorMutedForeground: '#383a3f',
    colorNeutral: '#131416',
    colorPrimary: '#3b6489',
    colorPrimaryForeground: '#ffffff',
    colorInput: '#e2e4e8',
    colorInputForeground: '#131416',
    colorDanger: '#953723',
    colorSuccess: '#24603a',
    colorWarning: '#6b4708',
    colorRing: '#3b6489',
    colorModalBackdrop: 'rgba(19, 20, 22, 0.28)',
  },
  dark: {
    ...SHARED,
    colorBackground: '#212226',
    colorForeground: '#e4e4e7',
    colorMutedForeground: '#a6a7ab',
    colorNeutral: '#ffffff',
    colorPrimary: '#94bce3',
    colorPrimaryForeground: '#1d2d3d',
    colorInput: '#1a1b1e',
    colorInputForeground: '#e4e4e7',
    colorDanger: '#e59b8c',
    colorSuccess: '#8fc99a',
    colorWarning: '#e2b765',
    colorRing: '#94bce3',
    colorModalBackdrop: 'rgba(9, 10, 11, 0.62)',
  },
};

/**
 * The share of its own alpha each colour kept under Clerk Core 2, which
 * the console used until October 2026 (`@clerk/clerk-react` 5): clerk-js 5
 * faded `colorRing` to 15% and `colorModalBackdrop` to 73% of what it was
 * given. Core 3 (`@clerk/react` 6, `@clerk/ui`) paints both exactly as
 * given, so they are handed over pre-faded and the focus ring and the
 * scrim behind Clerk's dialogs look as they did before the move.
 */
export const CORE2_FADE = { colorRing: 0.15, colorModalBackdrop: 0.73 } as const;

/** `#rrggbb` or `rgba(r, g, b, a)` (the two forms PALETTE uses) with its
 *  alpha multiplied by `share`. Anything else throws: a colour this cannot
 *  read must fail the tests, not reach Clerk unfaded. */
export function fade(colour: string, share: number): string {
  const hex = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(colour);
  const rgba = /^rgba\(\s*(\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\s*\)$/.exec(colour);
  let rgb: number[];
  let alpha: number;
  if (hex) {
    rgb = hex.slice(1, 4).map((h) => parseInt(h, 16));
    alpha = 1;
  } else if (rgba) {
    rgb = rgba.slice(1, 4).map(Number);
    alpha = Number(rgba[4]);
  } else {
    throw new Error(`fade: cannot read ${colour}`);
  }
  return `rgba(${rgb.join(', ')}, ${Number((alpha * share).toFixed(4))})`;
}

/** The variables as Clerk is handed them: PALETTE, the ring and scrim faded. */
function handed(variables: Variables): Variables {
  const out: Variables = { ...variables };
  for (const [name, share] of Object.entries(CORE2_FADE) as [keyof typeof CORE2_FADE, number][]) {
    const value = variables[name];
    if (typeof value === 'string') out[name] = fade(value, share);
  }
  return out;
}

/**
 * One object per scheme, built once. @clerk/react watches the provider's
 * `appearance` by identity and pushes every change down into clerk-js,
 * so a fresh object each render would re-paint Clerk on every render of
 * the app — which is why this is a lookup and not a constructor.
 */
/** The layer `index.css` orders below Tailwind's utilities, so a class
 *  passed through `appearance.elements` can actually win — see there. */
const CSS_LAYER = 'clerk';

/** Core 2's default, kept: Core 3 hides a form's optional fields unless told. */
const OPTIONS: Appearance['options'] = { showOptionalFields: true };

const APPEARANCE: Record<Scheme, Appearance> = {
  light: { cssLayerName: CSS_LAYER, options: OPTIONS, variables: handed(PALETTE.light) },
  dark: { cssLayerName: CSS_LAYER, options: OPTIONS, variables: handed(PALETTE.dark) },
};

/** The `appearance` for ClerkProvider under the given scheme. */
export function clerkAppearance(scheme: Scheme): Appearance {
  return APPEARANCE[scheme];
}
